/**
 * frontend/src/pages/Landing.js
 * 
 * Public landing page for ChangeFox.
 * It showcases features, workflow steps, and provides navigation to login/register.
 * Uses Framer Motion for animations and React Bootstrap for layout.
 * 
 * Common functions:
 * - Landing: Main component rendering the landing page sections.
 * - handleLogout: Clears user session if an authenticated user visits the landing page.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
  FiShield, FiUsers, FiFileText, FiActivity,
  FiCheckCircle, FiArrowRight, FiLock, FiZap,
  FiLayers, FiGitPullRequest, FiLogOut
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import BackgroundShapes from '../components/common/BackgroundShapes';
import Footer from '../components/common/Footer';
import SEO from '../components/common/SEO';
import logo from '../assets/logo.png';
import '../styles/landing.css';

const features = [
  {
    icon: <FiFileText />,
    title: 'Change Requests',
    description: 'Submit and track compliance change requests with detailed forms capturing all necessary information.',
    color: '#6366f1',
  },
  {
    icon: <FiGitPullRequest />,
    title: 'Status Workflow',
    description: 'Streamlined approval workflow from pending to implementation with clear visibility at each stage.',
    color: '#8b5cf6',
  },
  {
    icon: <FiActivity />,
    title: 'Real-time Logs',
    description: 'Live activity feed showing every action, comment, and status change as it happens.',
    color: '#06b6d4',
  },
  {
    icon: <FiUsers />,
    title: 'Team Collaboration',
    description: 'Invite team members, assign roles, and collaborate seamlessly on compliance changes.',
    color: '#10b981',
  },
  {
    icon: <FiShield />,
    title: 'Role-based Access',
    description: 'Granular permissions with admin and member roles to control who can approve changes.',
    color: '#f59e0b',
  },
  {
    icon: <FiLayers />,
    title: 'Multi-workspace',
    description: 'Organize your compliance efforts across multiple workspaces for different teams or projects.',
    color: '#ec4899',
  },
];

const steps = [
  {
    number: '01',
    title: 'Create Workspace',
    description: 'Set up your compliance workspace and invite your team members with a simple invite code.',
  },
  {
    number: '02',
    title: 'Submit Changes',
    description: 'Team members submit change requests with type, impact level, and detailed descriptions.',
  },
  {
    number: '03',
    title: 'Review & Approve',
    description: 'Admins review requests, update statuses, and move changes through the approval workflow.',
  },
  {
    number: '04',
    title: 'Track & Audit',
    description: 'Complete audit trail of all changes with timestamps, comments, and user attribution.',
  },
];

const stats = [
  { value: '99.9%', label: 'Uptime' },
  { value: '10x', label: 'Faster Reviews' },
  { value: '100%', label: 'Audit Ready' },
  { value: '24/7', label: 'Access' },
];

const Landing = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="landing-page">
      <SEO
        title="ChangeFox - Compliance Change Management Platform"
        description="Streamline your compliance change management with ChangeFox. Track requests, manage approvals, and maintain complete audit trails - all in one intuitive platform."
        keywords="compliance, change management, audit trail, change requests, approval workflow, compliance software, regulatory compliance, SOC 2, GDPR"
        path="/"
      />
      <BackgroundShapes />

      {/* Navigation */}
      <motion.nav
        className="landing-nav"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Container>
          <div className="nav-content">
            <Link to="/" className="nav-logo">
              <img src={logo} alt="ChangeFox" className="nav-logo-img" />
            </Link>
            <div className="nav-links">
              {user ? (
                <>
                  <Link to="/dashboard" className="nav-link">Dashboard</Link>
                  <Link to="/dashboard/settings" className="nav-link">Settings</Link>
                  <button onClick={handleLogout} className="btn-nav-signout">
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <a href="#features" className="nav-link">Product</a>
                  <a href="#how-it-works" className="nav-link">Resources</a>
                  <Link to="/pricing" className="nav-link">Pricing</Link>
                  <Link to="/login" className="nav-link">Sign In</Link>
                  <Link to="/register">
                    <Button className="btn-nav-cta">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </Container>
      </motion.nav>

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row className="align-items-center min-vh-100">
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
              >
                <div className="hero-badge">
                  <FiZap className="me-2" />
                  Compliance Made Simple
                </div>
                <h1 className="hero-title">
                  Manage Change
                  <span className="gradient-text"> Requests</span>
                  <br />with Confidence
                </h1>
                <p className="hero-subtitle">
                  Streamline your compliance change management process with our intuitive platform.
                  Track requests, manage approvals, and maintain complete audit trails—all in one place.
                </p>
                <div className="hero-cta">
                  {user ? (
                    <Link to="/dashboard">
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Button className="btn-hero-primary">
                          Go to Dashboard
                          <FiArrowRight className="ms-2" />
                        </Button>
                      </motion.div>
                    </Link>
                  ) : (
                    <>
                      <Link to="/register">
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                          <Button className="btn-hero-primary">
                            Start Free Trial
                            <FiArrowRight className="ms-2" />
                          </Button>
                        </motion.div>
                      </Link>
                      <Link to="/login">
                        <Button className="btn-hero-secondary">
                          Sign In
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
                <div className="hero-trust">
                  <FiLock className="me-2" />
                  <span>Enterprise-grade security • No credit card required</span>
                </div>
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div
                className="hero-visual"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                <div className="dashboard-preview">
                  <div className="preview-header">
                    <div className="preview-dots">
                      <span></span><span></span><span></span>
                    </div>
                    <span className="preview-title">ChangeFox Dashboard</span>
                  </div>
                  <div className="preview-content">
                    <div className="preview-sidebar">
                      <div className="preview-nav-item active"></div>
                      <div className="preview-nav-item"></div>
                      <div className="preview-nav-item"></div>
                    </div>
                    <div className="preview-main">
                      <div className="preview-card">
                        <div className="preview-card-header">
                          <div className="preview-card-title"></div>
                          <div className="preview-badge pending"></div>
                        </div>
                        <div className="preview-card-body"></div>
                      </div>
                      <div className="preview-card">
                        <div className="preview-card-header">
                          <div className="preview-card-title"></div>
                          <div className="preview-badge approved"></div>
                        </div>
                        <div className="preview-card-body"></div>
                      </div>
                      <div className="preview-card">
                        <div className="preview-card-header">
                          <div className="preview-card-title"></div>
                          <div className="preview-badge review"></div>
                        </div>
                        <div className="preview-card-body"></div>
                      </div>
                    </div>
                    <div className="preview-activity">
                      <div className="activity-header"></div>
                      <div className="activity-item"></div>
                      <div className="activity-item"></div>
                      <div className="activity-item"></div>
                      <div className="activity-item"></div>
                    </div>
                  </div>
                </div>
                <motion.div
                  className="floating-card card-1"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <FiCheckCircle className="text-success" />
                  <span>Request Approved</span>
                </motion.div>
                <motion.div
                  className="floating-card card-2"
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  <FiActivity className="text-primary" />
                  <span>12 Updates Today</span>
                </motion.div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <Container>
          <motion.div
            className="stats-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {stats.map((stat, index) => (
              <motion.div key={index} className="stat-item" variants={itemVariants}>
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </section>

      {/* Features Section */}
      <section className="features-section" id="features">
        <Container>
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="section-badge">Features</div>
            <h2 className="section-title">
              Everything you need for
              <span className="gradient-text"> compliance management</span>
            </h2>
            <p className="section-subtitle">
              Powerful features designed to streamline your change management workflow
              and keep your organization audit-ready.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <Row className="g-4">
              {features.map((feature, index) => (
                <Col key={index} md={6} lg={4}>
                  <motion.div
                    className="feature-card"
                    variants={itemVariants}
                    whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  >
                    <div
                      className="feature-icon"
                      style={{ background: `${feature.color}15`, color: feature.color }}
                    >
                      {feature.icon}
                    </div>
                    <h3 className="feature-title">{feature.title}</h3>
                    <p className="feature-description">{feature.description}</p>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* How it Works Section */}
      <section className="how-it-works-section" id="how-it-works">
        <Container>
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="section-badge light">Process</div>
            <h2 className="section-title light">
              Simple 4-step
              <span className="gradient-text"> workflow</span>
            </h2>
            <p className="section-subtitle light">
              Get started in minutes and have your compliance change management
              running smoothly in no time.
            </p>
          </motion.div>

          <motion.div
            className="steps-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {steps.map((step, index) => (
              <motion.div key={index} className="step-card" variants={itemVariants}>
                <div className="step-number">{step.number}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
                {index < steps.length - 1 && <div className="step-connector" />}
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <Container>
          <motion.div
            className="cta-card"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <div className="cta-content">
              <h2 className="cta-title">
                Ready to streamline your
                <br />
                <span className="gradient-text">compliance management?</span>
              </h2>
              <p className="cta-subtitle">
                Join teams who trust ChangeFox for their change management needs.
                Start your free trial today.
              </p>
              <div className="cta-buttons">
                <Link to="/register">
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button className="btn-cta-primary">
                      Get Started Free
                      <FiArrowRight className="ms-2" />
                    </Button>
                  </motion.div>
                </Link>
              </div>
              <div className="cta-features">
                <div className="cta-feature">
                  <FiCheckCircle />
                  <span>Free 14-day trial</span>
                </div>
                <div className="cta-feature">
                  <FiCheckCircle />
                  <span>No credit card</span>
                </div>
                <div className="cta-feature">
                  <FiCheckCircle />
                  <span>Cancel anytime</span>
                </div>
              </div>
            </div>
            <div className="cta-decoration">
              <motion.div
                className="deco-circle c1"
                animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 4, repeat: Infinity }}
              />
              <motion.div
                className="deco-circle c2"
                animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }}
                transition={{ duration: 5, repeat: Infinity }}
              />
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Landing;
